import { apiUrl } from './apiUrl';

async function parseJsonResponse(resp) {
  const text = await resp.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function apiErrorMessage(resp, payload, fallback) {
  if (resp.status === 404) {
    return 'Upload API not deployed. Redeploy the backend (prepareYogaMediaUpload / confirmYogaMediaUpload).';
  }
  if (resp.status === 413) {
    return 'File too large for API Gateway. Use presigned upload — redeploy the latest frontend build.';
  }
  return payload?.error || fallback;
}

/**
 * Upload yoga media via presigned S3 URLs (bypasses API Gateway body size limits).
 * Flow: prepare (JSON) → PUT file to S3 → confirm (save filename in game table).
 */
export async function uploadYogaMediaViaPresign({ files, userId, time, token, gameId }) {
  const fileMetas = files.map((file) => ({
    originalName: file.name,
    contentType: file.type || '',
    size: file.size,
  }));

  let prepareResp;
  try {
    prepareResp = await fetch(apiUrl('/prepareYogaMediaUpload'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, files: fileMetas, ...(gameId ? { gameId } : {}) }),
    });
  } catch (err) {
    throw new Error(
      err?.message === 'Failed to fetch'
        ? 'Cannot reach upload API. Check REACT_APP_BACKEND_URL and redeploy backend + frontend.'
        : String(err.message || err)
    );
  }

  const preparePayload = await parseJsonResponse(prepareResp);
  if (!prepareResp.ok) {
    throw new Error(apiErrorMessage(prepareResp, preparePayload, 'Could not prepare upload.'));
  }

  const uploads = Array.isArray(preparePayload?.uploads) ? preparePayload.uploads : [];
  if (uploads.length !== files.length) {
    throw new Error('Upload preparation mismatch.');
  }

  const filenames = [];

  for (let i = 0; i < uploads.length; i += 1) {
    const slot = uploads[i];
    const file = files[i];
    let putResp;
    try {
      putResp = await fetch(slot.uploadUrl, {
        method: slot.method || 'PUT',
        headers: { 'Content-Type': slot.contentType },
        body: file,
      });
    } catch (err) {
      throw new Error(
        `Direct S3 upload failed for ${file.name}. Add S3 bucket CORS (see backend/sql/s3_cors_yoga_upload.json). ${err.message || ''}`.trim()
      );
    }

    if (!putResp.ok) {
      throw new Error(
        `S3 rejected upload for ${file.name} (${putResp.status}). Check bucket CORS and IAM permissions.`
      );
    }

    filenames.push(slot.filename);
  }

  let confirmResp;
  try {
    confirmResp = await fetch(apiUrl('/confirmYogaMediaUpload'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        time,
        filenames,
        ...(gameId ? { gameId } : {}),
      }),
    });
  } catch (err) {
    throw new Error(
      `File uploaded to S3 but confirm failed: ${err.message || err}. Contact support with filenames: ${filenames.join(', ')}`
    );
  }

  const confirmPayload = await parseJsonResponse(confirmResp);
  if (!confirmResp.ok) {
    throw new Error(apiErrorMessage(confirmResp, confirmPayload, 'Could not confirm upload.'));
  }

  return confirmPayload;
}
